import { useGetProfilesAll } from '@/hooks/use-get-profiles-all';
import { Button, Spiner, Typography } from '@/ui-kit/';
import { CardSwiperUserNew } from '@/components/card-swiper-user-new';
import { useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { userProfilesAll } from '@/store/userProfilesAll';
import { useAuth } from '@/store/useAuth';
import { routesApp } from '@/constans/routes';
import { useNavigate } from 'react-router-dom';
import { eventsApi } from '@/api';
import { useProfilesEndedEvent } from '@/store/useProfilesEndedEvent';
import { callYaMetrika } from '@/utils/call-ya-metrika';
import { useSwipesStats } from '@/store/useSwipesStats';
import { TimeBetweenSwipesCalculator } from '@/utils/time-between-swipes-calculator';
import { MissMatch } from '@/components/miss-match';

export function Browse() {
    const { prevUsers, usersList, setPrevUsers, setUserList } = userProfilesAll();
    const { data, isFetching, refetch, error, } = useGetProfilesAll();
    const userData = useAuth((state) => state.getUser());
    const navigate = useNavigate();
    const [isProfilesRefetched, setProfilesRefetched] = useState(false);

    useEffect(() => {
        if (error) {
            const axiosError = error as any;
            if (axiosError.response?.status === 403 && axiosError.response?.data?.is_banned) {
                navigate(routesApp.banned);
            }
        }

        // reset cache users list if user deactivated
        if (userData?.active === false) {
            setUserList([]);
            setPrevUsers([]);
        } else if (data?.data) {
            setUserList(data.data.profiles); // Подставляем полученные данные в стейт
        }
    }, [data, setPrevUsers, error]);

    const [isScreenWasLoaded, setScreenWasLoaded] = useState(false);
    const { userId, setUserId } = useSwipesStats(state => state);
    
    useEffect(
        () => {
            if (!isFetching && !isScreenWasLoaded) {
                setScreenWasLoaded(true)
                TimeBetweenSwipesCalculator.reset()
                TimeBetweenSwipesCalculator.startTimer()

                const event = 'entered_card_view'
                const payload = {
                    user_id: userData?.id,
                    timestamp: Date.now()
                }
                callYaMetrika(event, payload)
                eventsApi.updateUserEvent(event, payload)
            }
        },
        [isFetching, isScreenWasLoaded]
    )

    useEffect(() => {
        if (userData)
            setUserId(userData.id)
    }, [userData])
    
    useEffect(() => {
        return () => {
            const event = 'left_card_view'
            const payload = {
                user_id: userId,
                timestamp: Date.now()
            }
            callYaMetrika(event, payload)
            eventsApi.updateUserEvent(event, payload)

            TimeBetweenSwipesCalculator.reset()
        }
    }, [userId])

    const newProfiles = () => {
        setProfilesRefetched(true)
        setPrevUsers([...usersList, ...prevUsers]); // Подставляем полученные данные в стейт
        refetch().then(() => setProfilesRefetched(false));
    };

    const [isFormsWasLoaded, setFormsWasLoaded] = useState(false);
    const {
        isSwiped,
        setSwiped,
        isFiltersWasApplied,
        setFiltersWasApplied,
    } =
        useProfilesEndedEvent(
            (state) => state
        );

    const isProfilesEnded = (usersCount: number) => 
        usersCount === 0 &&
        !data?.data.swipe_limit_reached

    const [isMissMatch, setMissMatch] = useState(false);

    useEffect(() => {
        if (
            !isFetching &&
            data &&
            isFiltersWasApplied
        ) {
            if (isProfilesEnded(data.data.profiles.length)) {
                setFiltersWasApplied(false)
                eventsApi
                    .updateUserEvent('profiles_ended')
            }
            TimeBetweenSwipesCalculator.reset()
            TimeBetweenSwipesCalculator.startTimer()
        }
    }, [data, isFetching, isFiltersWasApplied])

    useEffect(() => {
        if (
            !isFetching &&
            data &&
            !isFormsWasLoaded
        ) {
            setFormsWasLoaded(true)
            if (isProfilesEnded(data.data.profiles.length))
                eventsApi
                    .updateUserEvent('profiles_ended')
        }
    }, [data, isFetching, isFormsWasLoaded])

    useEffect(() => {
        if (
            !isFetching &&
            data &&
            isSwiped &&
            !isProfilesRefetched
        ) {
            setSwiped(false)
            const users =
                usersList?.filter(
                    (user) =>
                        !prevUsers.find(
                            (prevUser) =>
                                prevUser.id === user.id
                        )
                ) ?? []
            if (isProfilesEnded(users.length))
                eventsApi
                    .updateUserEvent('profiles_ended')
        }
    }, [data, isFetching, isSwiped, usersList, prevUsers, isProfilesRefetched])

    const users = useMemo(() => (usersList?.filter(
        (user) =>
            !prevUsers.find(
                (prevUser) =>
                    prevUser.id === user.id
            )
    ) ?? []), [usersList])

    const onBrokenMatch = () => setMissMatch(true)

    return (
        <>
            {
                isFetching ?
                    <Spiner />
                :
                    <CardSwiperUserNew
                        userData={userData}
                        users={users}
                        isSwipesLimited={data?.data.swipe_limit_reached ?? false}
                        refetchData={newProfiles}
                        onBrokenMatch={onBrokenMatch} />
            }
            {isMissMatch && (
                <MissMatch onAnimFinished={() => setMissMatch(false)} />
            )}
        </>
    )
}
